import gql from 'graphql-tag';

import {
    address,
    allCourseTerms,
    futureLectureTerm,
    lectureInstance,
    lectureTerm,
    officeClientBookingStats,
    officeClientWaitlistNotificationAttendee,
    officeEmployee,
    officePlace,
    simpleWorkshop,
    userReservations,
    userReservationsPublic,
} from '../fragments';

export const getAttendeesByLectureTermId = gql`
    query GetAttendeesByLectureTermId($officeId: Int!, $lectureTermId: Int!) {
        attendeesByLectureTermId(officeId: $officeId, lectureTermId: $lectureTermId) {
            ...UserReservationFragment
            user {
                id
                userId
                name
                email
                phone
                image
            }
            organisationClient {
                stats {
                    bookingStats {
                        ...OfficeClientBookingStatsFragment
                    }
                }
            }
        }
    }
    ${userReservations}
    ${officeClientBookingStats}
`;

export const getWaitlistNotificationAttendeesByLectureTermId = gql`
    query GetWaitlistNotificationAttendeesByLectureTermId(
        $officeId: Int!
        $lectureTermId: Int!
        $organisationId: Int!
    ) {
        waitlistNotificationAttendeesByLectureTermId(
            officeId: $officeId
            lectureTermId: $lectureTermId
            organisationId: $organisationId
        ) {
            ...OfficeClientWaitlistNotificationAttendeeFragment
        }
    }
    ${officeClientWaitlistNotificationAttendee}
`;

export const getCourseAttendeesByCourseId = gql`
    query GetCourseAttendeesByCourseId($officeId: Int!, $courseId: Int!) {
        courseAttendeesByCourseId(officeId: $officeId, courseId: $courseId) {
            ...UserReservationFragment
            user {
                id
                userId
                name
                email
                phone
                image
            }
            organisationClient {
                stats {
                    bookingStats {
                        ...OfficeClientBookingStatsFragment
                    }
                }
            }
        }
    }
    ${userReservations}
    ${officeClientBookingStats}
`;

export const getReservationsByLectureIdAndUserId = gql`
    query GetReservationsByLectureIdAndUserId(
        $userId: Int!
        $lectureTermId: Int!
        $filters: LectureTermReservationFiltersInput
    ) {
        reservationsByLectureTermIdAndUserId(userId: $userId, lectureTermId: $lectureTermId, filters: $filters) {
            ...UserReservationFragment
        }
    }
    ${userReservations}
`;

export const getPublicAttendeesByLectureTermId = gql`
    query GetAttendeesByLectureTermId($officeId: Int!, $lectureTermId: Int!) {
        publicAttendeesByLectureTermId(officeId: $officeId, lectureTermId: $lectureTermId) {
            ...UserReservationPublicFragment
        }
    }
    ${userReservationsPublic}
`;

export const getPublicCourseAttendeesByCourseId = gql`
    query GetPublicCourseAttendeesByCourseId($officeId: Int!, $courseId: Int!) {
        publicCourseAttendeesByCourseId(officeId: $officeId, courseId: $courseId) {
            ...UserReservationPublicFragment
        }
    }
    ${userReservationsPublic}
`;

export const getCourseTermsCapacities = gql`
    query GetCourseTermsCapacities($courseId: Int!) {
        courseTermsCapacities(courseId: $courseId) {
            lectureTermId
            activeReservationCount
        }
    }
`;

export const getTermOnlineData = gql`
    query GetTermOnlineData($officeId: Int!, $lectureTermId: Int!, $paidOrderId: Int) {
        getTermOnlineData(officeId: $officeId, lectureTermId: $lectureTermId, paidOrderId: $paidOrderId) {
            onlineLectureDescription
            videoLinkUrl
            videoLinkPassword
        }
    }
`;

/** *******/

export const getAllCourseTermsByLectureId = gql`
    query AllTermsForCourseByLectureId($lectureId: Int!) {
        allTermsForCourseByLectureId(lectureId: $lectureId) {
            ...AllCourseTermsFragment
        }
    }
    ${allCourseTerms}
`;

export const getFutureLectureTermsByLectureId = gql`
    query FutureLectureTermsByLectureId($officeId: Int!, $lectureId: Int!, $lectureTermId: Int!) {
        futureLectureTermsByLectureId(officeId: $officeId, lectureId: $lectureId, lectureTermId: $lectureTermId) {
            ...FutureLectureTermFragment
        }
    }
    ${futureLectureTerm}
`;

export const getOfficePrivateLecturesByDatetimeRange = gql`
    query OfficePrivateLecturesByDatetimeRange(
        $officeId: Int!
        $weekStart: DateTime!
        $weekEnd: DateTime!
        $filters: OfficePublicLecturesFiltersInput
    ) {
        officePrivateLecturesByDatetimeRange(
            officeId: $officeId
            weekStart: $weekStart
            weekEnd: $weekEnd
            filters: $filters
        ) {
            ...LectureTermFragment
            lectureInstance {
                ...LectureInstanceFragment
                place {
                    ...OfficePlaceFragment
                }
                lecturer {
                    ...OfficeEmployeeFragment
                }
            }
        }
    }
    ${lectureTerm}
    ${lectureInstance}
    ${officePlace}
    ${officeEmployee}
`;

export const getPrivateLectureByGraphQlId = gql`
    query OfficePrivateLectureByGraphQlId($officeId: Int!, $graphQlId: String!) {
        privateLectureTermByGraphQlId(officeId: $officeId, graphQlId: $graphQlId) {
            ...LectureTermFragment
            lectureInstance {
                ...LectureInstanceFragment
                place {
                    ...OfficePlaceFragment
                }
                lecturer {
                    ...OfficeEmployeeFragment
                }
            }
        }
    }
    ${lectureTerm}
    ${lectureInstance}
    ${officePlace}
    ${officeEmployee}
`;

export const getOfficePublicLecturesDates = gql`
    query OfficePublicLecturesDates($officeId: Int!, $weekStart: DateTime!) {
        officePublicLecturesDates(officeId: $officeId, weekStart: $weekStart) {
            start
        }
    }
`;

export const getOfficePublicLecturesByDatetimeRange = gql`
    query OfficePublicLecturesByDatetimeRange(
        $officeId: Int!
        $weekStart: DateTime!
        $weekEnd: DateTime!
        $filters: OfficePublicLecturesFiltersInput
    ) {
        officePublicLecturesByDatetimeRange(
            officeId: $officeId
            weekStart: $weekStart
            weekEnd: $weekEnd
            filters: $filters
        ) {
            ...LectureTermFragment
            lectureInstance {
                ...LectureInstanceFragment
                place {
                    ...OfficePlaceFragment
                }
                lecturer {
                    ...OfficeEmployeeFragment
                }
            }
        }
    }
    ${lectureTerm}
    ${lectureInstance}
    ${officePlace}
    ${officeEmployee}
`;

export const getOfficeLectures = gql`
    query OfficeLectures($officeId: Int!, $filters: OfficeLecturesFiltersInput, $limit: Int, $offset: Int) {
        officeLectures(officeId: $officeId, filters: $filters, limit: $limit, offset: $offset) {
            lectures {
                ...LectureTermFragment
                lectureInstance {
                    ...LectureInstanceFragment
                    place {
                        ...OfficePlaceFragment
                    }
                    lecturer {
                        ...OfficeEmployeeFragment
                    }
                }
            }
            totalCount
        }
    }
    ${lectureTerm}
    ${lectureInstance}
    ${officePlace}
    ${officeEmployee}
`;

export const getMyOfficeLectures = gql`
    query MyOfficeLectures($officeId: Int!, $userId: Int!, $LECTURE_START_FROM: DateTime, $limit: Int, $offset: Int) {
        myOfficeLectures(
            officeId: $officeId
            userId: $userId
            LECTURE_START_FROM: $LECTURE_START_FROM
            limit: $limit
            offset: $offset
        ) {
            lectures {
                ...LectureTermFragment
                lectureInstance {
                    ...LectureInstanceFragment
                    place {
                        ...OfficePlaceFragment
                    }
                    lecturer {
                        ...OfficeEmployeeFragment
                    }
                }
            }
            totalCount
        }
    }
    ${lectureTerm}
    ${lectureInstance}
    ${officePlace}
    ${officeEmployee}
`;

export const getMyLectures = gql`
    query MyLectures($userId: Int!, $filters: MyLecturesFiltersInput) {
        myLectures(userId: $userId, filters: $filters) {
            ...LectureTermFragment
            lectureInstance {
                ...LectureInstanceFragment
                place {
                    ...OfficePlaceFragment
                }
                lecturer {
                    ...OfficeEmployeeFragment
                }
            }
        }
    }
    ${lectureTerm}
    ${lectureInstance}
    ${officePlace}
    ${officeEmployee}
`;

export const getMyWorkshops = gql`
    query MyWorkshops($userId: Int!, $filters: MyWorkshopsFiltersInput) {
        myWorkshops(userId: $userId, filters: $filters) {
            ...WorkshopSimpleFragment
        }
    }
    ${simpleWorkshop}
`;

export const getWorkshopsReservationsByUserId = gql`
    query WorkshopsReservationsByUserId($userId: Int!, $filters: WorkshopReservationsByUserIdFiltersInput) {
        workshopsReservationsByUserId(userId: $userId, filters: $filters) {
            ...WorkshopSimpleFragment
        }
    }
    ${simpleWorkshop}
`;

export const getOfficeLectureTermById = gql`
    query OfficeLectureTermById($officeId: Int!, $lectureId: Int!) {
        officeLectureTermById(officeId: $officeId, lectureId: $lectureId) {
            ...LectureTermFragment
            lectureInstance {
                ...LectureInstanceFragment
                place {
                    ...OfficePlaceFragment
                    address {
                        ...AddressFragment
                    }
                }
                lecturer {
                    ...OfficeEmployeeFragment
                }
            }
        }
    }
    ${lectureTerm}
    ${lectureInstance}
    ${officePlace}
    ${address}
    ${officeEmployee}
`;

export const getPublicLectureTermByGraphQlId = gql`
    query PublicLectureTermByGraphQlId($graphQlId: String!) {
        publicLectureTermByGraphQlId(graphQlId: $graphQlId) {
            ...LectureTermFragment
            lectureInstance {
                ...LectureInstanceFragment
                place {
                    ...OfficePlaceFragment
                    address {
                        ...AddressFragment
                    }
                }
                lecturer {
                    ...OfficeEmployeeFragment
                }
            }
        }
    }
    ${lectureTerm}
    ${lectureInstance}
    ${officePlace}
    ${address}
    ${officeEmployee}
`;
